/**
 * WORK ITEM TYPE CONFIG LOADER
 *
 * Loads static YAML configuration from types.yaml during application startup.
 * Maintains an in-memory registry for fast lookups.
 *
 * Pattern matches config/industry-solutions/configLoader.ts
 */

import type {
  WorkItemTypeDefinition,
  WorkItemTypesConfig,
  FinanceGroup,
  StatusDefinitionEntry,
  StatusDefinition,
  SystemStatusRule,
  UserAction,
  StatusDomain,
  StatusVisibilityContext,
} from './types';

// ============================================================================
// CONFIGURATION REGISTRY
// ============================================================================

class WorkItemTypeConfigRegistry {
  private typesByKey = new Map<string, WorkItemTypeDefinition>();
  private typesById = new Map<number, WorkItemTypeDefinition>();
  private typesByCategory = new Map<string, WorkItemTypeDefinition[]>();
  private financeTypes: WorkItemTypeDefinition[] = [];
  private initialized = false;

  // Status definition indexes
  private statusDefsByItemType = new Map<string, StatusDefinitionEntry>();
  private statusDefByKey = new Map<string, StatusDefinition>();
  private statusDefByItemTypeAndKey = new Map<string, StatusDefinition>();
  private systemStatusesByItemType = new Map<string, Set<string>>();
  private terminalStatusesByItemType = new Map<string, Set<string>>();
  private payableTypesSet = new Set<string>();
  private quotableTypesSet = new Set<string>();
  private financeItemTypes = new Set<string>();

  /**
   * Load parsed configuration into the registry.
   * Called by initializeWorkItemTypeRegistry() in configLoader.server.ts
   * after reading and parsing the YAML file on the server side.
   */
  loadConfig(config: WorkItemTypesConfig): void {
    if (this.initialized) {
      return;
    }

    if (!config || !Array.isArray(config.types)) {
      console.error('[WorkItemTypeConfig] Invalid types.yaml structure');
      this.initialized = true;
      return;
    }

      for (const def of config.types) {
        this.typesByKey.set(def.type_key, def);
        this.typesById.set(def.id, def);

        const categoryList = this.typesByCategory.get(def.category) || [];
        categoryList.push(def);
        this.typesByCategory.set(def.category, categoryList);

        if (def.used_by_finance) {
          this.financeTypes.push(def);
        }
      }

      // Index status definitions
      if (Array.isArray(config.status_definitions)) {
        for (const entry of config.status_definitions) {
          this.statusDefsByItemType.set(entry.item_type, entry);

          if (entry.domain === 'finance') {
            this.financeItemTypes.add(entry.item_type);
          }

          const sysSet = new Set<string>();
          const termSet = new Set<string>();

          for (const status of entry.statuses) {
            const compositeKey = `${entry.item_type}:${status.key}`;
            this.statusDefByKey.set(compositeKey, status);
            this.statusDefByItemTypeAndKey.set(compositeKey, status);

            if (status.is_system) {
              sysSet.add(status.key);
            }
            if (status.is_terminal) {
              termSet.add(status.key);
            }
          }

          this.systemStatusesByItemType.set(entry.item_type, sysSet);
          this.terminalStatusesByItemType.set(entry.item_type, termSet);

          if (entry.payable_types) {
            for (const pt of entry.payable_types) {
              this.payableTypesSet.add(pt);
            }
          }
          if (entry.quotable_types) {
            for (const qt of entry.quotable_types) {
              this.quotableTypesSet.add(qt);
            }
          }
        }
      }

    this.initialized = true;
    console.log(
      `[WorkItemTypeConfig] ✓ Loaded ${this.typesByKey.size} types, ${this.financeTypes.length} finance types, ${this.statusDefsByItemType.size} status definitions`
    );
  }

  /**
   * Get a work item type definition by its type_key
   */
  getType(typeKey: string): WorkItemTypeDefinition | null {
    return this.typesByKey.get(typeKey) || null;
  }

  /**
   * Get a work item type definition by its numeric id
   */
  getTypeById(id: number): WorkItemTypeDefinition | null {
    return this.typesById.get(id) || null;
  }

  /**
   * Get all type definitions for a given category
   */
  getTypesByCategory(category: string): WorkItemTypeDefinition[] {
    return this.typesByCategory.get(category) || [];
  }

  /**
   * Get all work item type definitions
   */
  getAllTypes(): WorkItemTypeDefinition[] {
    return Array.from(this.typesByKey.values());
  }

  /**
   * Get all finance-eligible type definitions
   */
  getFinanceTypes(): WorkItemTypeDefinition[] {
    return this.financeTypes;
  }

  /**
   * Get finance-eligible type definitions filtered by finance group
   */
  getFinanceTypesByGroup(group: FinanceGroup): WorkItemTypeDefinition[] {
    return this.financeTypes.filter((t) => t.finance_group === group);
  }

  /**
   * Check if a type_key exists in the registry
   */
  hasType(typeKey: string): boolean {
    return this.typesByKey.has(typeKey);
  }

  /**
   * Check if the registry has been initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  // ==========================================================================
  // STATUS DEFINITION LOOKUP METHODS
  // ==========================================================================

  /**
   * Determine the fallback item_type based on domain.
   * Finance types fall back to '_generic', operational types to '_operational_generic'.
   */
  private getFallbackItemType(itemType: string): string {
    return this.financeItemTypes.has(itemType) ? '_generic' : '_operational_generic';
  }

  /**
   * Get the full StatusDefinitionEntry for an item_type.
   * Falls back to domain-appropriate generic entry if not found.
   */  getStatusDefinitionEntry(itemType: string): StatusDefinitionEntry | null {
    return this.statusDefsByItemType.get(itemType)
      || this.statusDefsByItemType.get(this.getFallbackItemType(itemType))
      || this.statusDefsByItemType.get('_generic')
      || null;
  }

  /**
   * Get a specific StatusDefinition by item_type and status key.
   */
  getStatusDefinition(itemType: string, statusKey: string): StatusDefinition | null {
    const compositeKey = `${itemType}:${statusKey}`;
    return this.statusDefByItemTypeAndKey.get(compositeKey) || null;
  }

  /**
   * Get all statuses for an item_type.
   */
  getStatuses(itemType: string): StatusDefinition[] {
    const entry = this.getStatusDefinitionEntry(itemType);
    return entry ? entry.statuses : [];
  }

  /**
   * Get system rules for an item_type (payable document rules).
   */
  getSystemRules(itemType: string): SystemStatusRule[] {
    const entry = this.getStatusDefinitionEntry(itemType);
    return entry?.system_rules || [];
  }

  /**
   * Get quotable rules for an item_type (expiry-based rules).
   */
  getQuotableRules(itemType: string): SystemStatusRule[] {
    const entry = this.getStatusDefinitionEntry(itemType);
    return entry?.quotable_rules || [];
  }

  /**
   * Get user actions for an item_type at a given current status.
   */
  getUserActions(itemType: string, currentStatus: string): UserAction[] {
    const entry = this.getStatusDefinitionEntry(itemType);
    if (!entry?.user_actions) return [];
    const actions = entry.user_actions[currentStatus];
    return actions ? [...actions] : [];
  }

  /**
   * Check if a status key is system-managed for a given item_type.
   */
  isSystemStatus(itemType: string, statusKey: string): boolean {
    const sysSet = this.systemStatusesByItemType.get(itemType)
      || this.systemStatusesByItemType.get(this.getFallbackItemType(itemType))
      || this.systemStatusesByItemType.get('_generic');
    return sysSet ? sysSet.has(statusKey) : false;
  }

  /**
   * Check if a status key is terminal for a given item_type.
   */
  isTerminalStatus(itemType: string, statusKey: string): boolean {
    const termSet = this.terminalStatusesByItemType.get(itemType)
      || this.terminalStatusesByItemType.get(this.getFallbackItemType(itemType))
      || this.terminalStatusesByItemType.get('_generic');
    return termSet ? termSet.has(statusKey) : false;
  }

  /**
   * Get terminal statuses for an item_type.
   */
  getTerminalStatuses(itemType: string): string[] {
    const entry = this.getStatusDefinitionEntry(itemType);
    return entry?.terminal_statuses || [];
  }

  /**
   * Check if an item_type is payable (has system_rules for paid/partial/overdue).
   */
  isPayableType(itemType: string): boolean {
    return this.payableTypesSet.has(itemType);
  }

  /**
   * Check if an item_type is quotable (has quotable_rules for expiry).
   */
  isQuotableType(itemType: string): boolean {
    return this.quotableTypesSet.has(itemType);
  }

  /**
   * Check if an item_type belongs to the finance domain.
   */
  isFinanceDomain(itemType: string): boolean {
    return this.financeItemTypes.has(itemType);
  }

  /**
   * Get the domain for an item_type. Returns 'finance' or 'operational'.
   */
  getDomain(itemType: string): StatusDomain {
    return this.financeItemTypes.has(itemType) ? 'finance' : 'operational';
  }

  /**
   * Check if a status transition is allowed for an item_type.
   * Uses allowed_from on the target status definition if present,
   * otherwise falls back to user_actions.
   */
  isTransitionAllowed(itemType: string, fromStatus: string, toStatus: string): boolean {
    const targetDef = this.getStatusDefinition(itemType, toStatus);
    if (targetDef?.allowed_from) {
      return targetDef.allowed_from.includes(fromStatus);
    }
    const actions = this.getUserActions(itemType, fromStatus);
    return actions.some((a) => a.target_status === toStatus);
  }

  /**
   * Check if an item_type belongs to the finance domain (alias for isFinanceDomain).
   */
  isFinanceType(itemType: string): boolean {
    return this.isFinanceDomain(itemType);
  }

  /**
   * Get all statuses for an item_type (alias for getStatuses).
   */
  getStatusesForItemType(itemType: string): StatusDefinition[] {
    return this.getStatuses(itemType);
  }

  /**
   * Get statuses filtered by visible_in context.
   */
  getVisibleStatuses(itemType: string, context: StatusVisibilityContext): StatusDefinition[] {
    const statuses = this.getStatuses(itemType);
    return statuses.filter(s => !s.visible_in || s.visible_in.includes(context));
  }

  /**
   * Get statuses visible in the data grid for an item_type.
   */
  getDataGridVisibleStatuses(itemType: string): StatusDefinition[] {
    return this.getVisibleStatuses(itemType, 'data_grid');
  }

  /**
   * Get statuses visible in the admin sidebar for an item_type.
   */
  getAdminSidebarVisibleStatuses(itemType: string): StatusDefinition[] {
    return this.getVisibleStatuses(itemType, 'admin_sidebar');
  }

  /**
   * Get statuses visible in SmartTask for an item_type.
   */
  getSmartTaskVisibleStatuses(itemType: string): StatusDefinition[] {
    return this.getVisibleStatuses(itemType, 'smart_task');
  }

  /**
   * Get allowed transitions from a current status for an item_type.
   * Uses allowed_from on target statuses and user_actions.
   */
  getAllowedTransitions(itemType: string, currentStatus: string): string[] {
    const statuses = this.getStatuses(itemType);
    const allowed = new Set<string>();

    // Check allowed_from on each target status
    for (const s of statuses) {
      if (s.allowed_from?.includes(currentStatus)) {
        allowed.add(s.key);
      }
    }

    // Also check user_actions for the current status
    const actions = this.getUserActions(itemType, currentStatus);
    for (const a of actions) {
      allowed.add(a.target_status);
    }

    return Array.from(allowed);
  }

  /**
   * Get the color for a status key of an item_type.
   */
  getStatusColor(itemType: string, statusKey: string): string | null {
    const def = this.getStatusDefinition(itemType, statusKey);
    return def?.color || null;
  }

  /**
   * Get the label for a status key of an item_type.
   */
  getStatusLabel(itemType: string, statusKey: string): string | null {
    const def = this.getStatusDefinition(itemType, statusKey);
    if (def?.label) return def.label;
    // Try domain-aware fallback entry
    const entry = this.getStatusDefinitionEntry(itemType);
    if (entry) {
      const fallbackStatus = entry.statuses.find(s => s.key === statusKey);
      if (fallbackStatus?.label) return fallbackStatus.label;
    }
    return null;
  }

  /**
   * Get all terminal status keys across all operational type definitions.
   * Useful when itemType is unknown but the item is known to be operational.
   */
  getOperationalTerminalStatuses(): string[] {
    const result = new Set<string>();
    for (const [itemType, termSet] of this.terminalStatusesByItemType) {
      if (itemType === '_generic' || this.financeItemTypes.has(itemType)) continue;
      for (const s of termSet) result.add(s);
    }
    // Also include _operational_generic
    const opGeneric = this.terminalStatusesByItemType.get('_operational_generic');
    if (opGeneric) for (const s of opGeneric) result.add(s);
    return Array.from(result);
  }

  /**
   * Get all status keys across all operational type definitions.
   */
  getOperationalStatuses(): string[] {
    const result = new Set<string>();
    for (const [itemType, entry] of this.statusDefsByItemType) {
      if (itemType === '_generic' || this.financeItemTypes.has(itemType)) continue;
      for (const s of entry.statuses) result.add(s.key);
    }
    return Array.from(result);
  }

  /**
   * Get all status keys for a domain ('operational' or 'finance').
   */
  getStatusesForDomain(domain: StatusDomain): string[] {
    const result = new Set<string>();
    for (const [itemType, entry] of this.statusDefsByItemType) {
      if (domain === 'finance' && entry.domain !== 'finance') continue;
      if (domain === 'operational' && entry.domain !== 'operational') continue;
      for (const s of entry.statuses) result.add(s.key);
    }
    return Array.from(result);
  }

  /**
   * Check if a status key is terminal for any operational type.
   * Useful when itemType is unknown but the item is known to be operational.
   */
  isOperationalTerminalStatus(statusKey: string): boolean {
    for (const [itemType, termSet] of this.terminalStatusesByItemType) {
      if (itemType === '_generic' || this.financeItemTypes.has(itemType)) continue;
      if (termSet.has(statusKey)) return true;
    }
    const opGeneric = this.terminalStatusesByItemType.get('_operational_generic');
    return opGeneric ? opGeneric.has(statusKey) : false;
  }

  /**
   * Get the StatusDefinition for a status key from the operational generic fallback.
   * Useful when itemType is unknown but the item is operational.
   */
  getOperationalStatusDefinition(statusKey: string): StatusDefinition | null {
    const entry = this.statusDefsByItemType.get('_operational_generic');
    if (!entry) return null;
    return entry.statuses.find(s => s.key === statusKey) || null;
  }

  /**
   * Get all status keys in a specific operational category ('open', 'active', 'closed').
   * Uses the _operational_generic entry as the authoritative source.
   */
  getOperationalStatusesByCategory(category: 'open' | 'active' | 'closed'): string[] {
    const entry = this.statusDefsByItemType.get('_operational_generic');
    if (!entry) return [];
    return entry.statuses
      .filter(s => s.category === category)
      .map(s => s.key);
  }

  /**
   * Get all status keys in a specific workflow phase ('travel', 'work', 'site', 'upcoming').
   * Uses the _operational_generic entry as the authoritative source.
   */
  getOperationalStatusesByWorkflowPhase(phase: 'travel' | 'work' | 'site' | 'upcoming'): string[] {
    const entry = this.statusDefsByItemType.get('_operational_generic');
    if (!entry) return [];
    return entry.statuses
      .filter(s => s.workflow_phase === phase)
      .map(s => s.key);
  }

  /**
   * Get the workflow_phase for a status key from the operational generic fallback.
   */
  getOperationalWorkflowPhase(statusKey: string): 'travel' | 'work' | 'site' | 'upcoming' | null {
    const entry = this.statusDefsByItemType.get('_operational_generic');
    if (!entry) return null;
    const status = entry.statuses.find(s => s.key === statusKey);
    return status?.workflow_phase || null;
  }

  /**
   * Get the default visible tabs for a work item type from YAML config.
   * Returns null if the type has no default_visible_tabs defined.
   */
  getDefaultVisibleTabs(typeKey: string): string[] | null {
    const def = this.typesByKey.get(typeKey);
    if (!def || !def.default_visible_tabs || !Array.isArray(def.default_visible_tabs)) {
      return null;
    }
    return def.default_visible_tabs;
  }

  /**
   * Get the registry for external access (read-only)
   */
  getRegistry() {
    return {
      typesByKey: this.typesByKey,
      typesById: this.typesById,
      typesByCategory: this.typesByCategory,
      financeTypes: this.financeTypes,
      statusDefsByItemType: this.statusDefsByItemType,
    };
  }
}

// ============================================================================
// SINGLETON REGISTRY INSTANCE
// ============================================================================

let registryInstance: WorkItemTypeConfigRegistry | null = null;

export function getWorkItemTypeRegistry(): WorkItemTypeConfigRegistry {
  if (!registryInstance) {
    registryInstance = new WorkItemTypeConfigRegistry();
  }
  return registryInstance;
}

// Export for testing/debugging
export { WorkItemTypeConfigRegistry };
